import { useCallback, useEffect, useRef } from 'react';
import { useSimulationStore } from '../store/simulationStore';
import { useVersionStore } from '../store/versionStore';
import { AUTO_SAVE_DEBOUNCE_MS, AUTO_SAVE_IDLE_TIMEOUT_MS } from '../config/constants';

interface UseAutoSaveOptions {
    debounceMs?: number;
    enabled?: boolean;
}

const getErrorMessage = (error: unknown) => {
    if (error instanceof Error) return error.message;
    if (typeof error === 'string') return error;
    try {
        return JSON.stringify(error);
    } catch {
        return 'Unknown error';
    }
};

export const useAutoSave = ({ debounceMs = AUTO_SAVE_DEBOUNCE_MS, enabled = true }: UseAutoSaveOptions = {}) => {
    const graph = useSimulationStore((state) => state.graph);
    const currentSimulationId = useSimulationStore((state) => state.currentSimulationId);
    const currentSimulationName = useSimulationStore((state) => state.currentSimulationName);
    const currentVersionId = useSimulationStore((state) => state.currentVersionId);
    const setWorkspaceContext = useSimulationStore((state) => state.setWorkspaceContext);
    const setAutoSaveStatus = useSimulationStore((state) => state.setAutoSaveStatus);
    const setLastAutoSaveAt = useSimulationStore((state) => state.setLastAutoSaveAt);
    const autoSaveStatus = useSimulationStore((state) => state.autoSaveStatus);
    const lastAutoSaveAt = useSimulationStore((state) => state.lastAutoSaveAt);
    const autoSaveError = useSimulationStore((state) => state.autoSaveError);
    const hasActiveInputs = useSimulationStore((state) => state.hasActiveInputs);
    const activeInputsCount = useSimulationStore((state) => state.activeInputsCount);

    const saveVersion = useVersionStore((state) => state.saveVersion);
    const getSimulation = useVersionStore((state) => state.getSimulation);

    const pendingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastSavedDataUpdatedAt = useRef<string | undefined>(graph.dataUpdatedAt);

    const clearTimer = useCallback(() => {
        if (pendingTimerRef.current !== null) {
            clearTimeout(pendingTimerRef.current);
            pendingTimerRef.current = null;
        }
    }, []);

    useEffect(() => {
        return () => {
            clearTimer();
        };
    }, [clearTimer]);

    useEffect(() => {
        if (!currentSimulationId) {
            setAutoSaveStatus('idle');
        }
    }, [currentSimulationId, setAutoSaveStatus]);

    useEffect(() => {
        lastSavedDataUpdatedAt.current = graph.dataUpdatedAt;
    }, [currentSimulationId, currentVersionId]);

    const scheduleSave = useCallback(() => {
        if (!enabled) {
            return;
        }

        const simulationId = currentSimulationId;
        if (!simulationId) {
            return;
        }

        // Don't save if user is viewer
        const currentAccessLevel = useSimulationStore.getState().currentAccessLevel;
        if (currentAccessLevel === 'viewer') {
            return;
        }

        // Don't save if there are active input fields to prevent interfering with user input
        if (hasActiveInputs()) {
            return;
        }

        const dataUpdatedAt = graph.dataUpdatedAt;
        if (!dataUpdatedAt) {
            return;
        }

        if (lastSavedDataUpdatedAt.current === dataUpdatedAt) {
            return;
        }

        clearTimer();

        pendingTimerRef.current = setTimeout(async () => {
            pendingTimerRef.current = null;
            
            // Double-check that no inputs are active before saving
            // (user might have started typing after the timer was set)
            if (hasActiveInputs()) {
                return;
            }
            
            setAutoSaveStatus('saving');

            try {
                const version = await saveVersion(simulationId, graph, { autoSave: true });

                if (!version) {
                    setAutoSaveStatus('error', 'Auto-save failed');
                    return;
                }

                lastSavedDataUpdatedAt.current = version.graph.dataUpdatedAt ?? graph.dataUpdatedAt;
                setAutoSaveStatus('saved');
                setLastAutoSaveAt(version.created_at);

                const simulation = getSimulation(simulationId);
                const simulationName = simulation?.name ?? currentSimulationName;

                // Preserve access level when updating workspace context after save
                const currentAccessLevel = useSimulationStore.getState().currentAccessLevel;

                setWorkspaceContext(
                    simulationId,
                    simulationName,
                    version.id,
                    version.name,
                    currentAccessLevel
                );
            } catch (error) {
                setAutoSaveStatus('error', getErrorMessage(error));
            }
        }, debounceMs);
    }, [
        clearTimer,
        currentSimulationId,
        currentSimulationName,
        debounceMs,
        enabled,
        graph,
        saveVersion,
        setAutoSaveStatus,
        setLastAutoSaveAt,
        setWorkspaceContext,
        getSimulation,
        hasActiveInputs,
    ]);

    useEffect(() => {
        scheduleSave();

        return () => {
            clearTimer();
        };
    }, [scheduleSave, clearTimer, graph.dataUpdatedAt]);

    // Trigger save when all inputs become inactive (activeInputsCount goes from >0 to 0)
    // This handles the case where user finishes editing but graph.dataUpdatedAt didn't change
    useEffect(() => {
        if (activeInputsCount === 0 && graph.dataUpdatedAt) {
            // Small delay to ensure the blur event has fully processed
            const timeout = setTimeout(() => {
                scheduleSave();
            }, 100);
            return () => clearTimeout(timeout);
        }
    }, [activeInputsCount, scheduleSave, graph.dataUpdatedAt]);

    useEffect(() => {
        if (!enabled) {
            clearTimer();
        }
    }, [enabled, clearTimer]);

    useEffect(() => {
        if (autoSaveStatus !== 'saved') {
            return;
        }

        const timeout = setTimeout(() => {
            setAutoSaveStatus('idle');
        }, AUTO_SAVE_IDLE_TIMEOUT_MS);

        return () => {
            clearTimeout(timeout);
        };
    }, [autoSaveStatus, setAutoSaveStatus]);

    return {
        status: autoSaveStatus,
        lastSavedAt: lastAutoSaveAt,
        error: autoSaveError,
    };
};

